/**
 * A registry command's result in the operator's language: the EPP result code (RFC 5730 §3) as a
 * sentence (`common:eppResult.<code>`, else the success / failure of its class), the registry's
 * own text kept as the technical detail.
 *
 * Pure: tests/epp-result.test.mjs.
 */

/** RFC 5730 §3 result codes. */
export const EPP_RESULT_CODES = [
  1000, 1001, 1300, 1301, 1500, 2000, 2001, 2002, 2003, 2004, 2005, 2100, 2101, 2102, 2103, 2104, 2105, 2106, 2200, 2201, 2202, 2300, 2301, 2302, 2303, 2304, 2305, 2306, 2307, 2308,
  2400, 2500, 2501, 2502,
];

export function eppResultWith(code: number, message: string | null, lookup: (key: string) => string | undefined): { text: string; detail: string | null } {
  const text = lookup(`eppResult.${code}`) ?? lookup(code < 2000 ? 'eppResult.success' : 'eppResult.failure') ?? message ?? String(code);
  return { text, detail: message && message !== text ? message : null };
}

/** What a transfer's `reject_reason` says: the registry's answer, or a code the platform records, in words. */
export type TransferReason = { kind: 'registry'; code: number; message: string | null } | { kind: 'words'; text: string };

/**
 * A transfer's `reject_reason` (crates/lifecycle/src/transfer.rs): the registry's refusal
 * "<code>: <message>" (or "REGISTRY_REJECTED <code>") is its EPP result; a policy ground or a
 * failure code reads by its vocabulary (`domains:transfers.grounds.*`, `domains:vocab.failure.*`);
 * our own cancellation reads in words around the staff's reason; anything else is what a person
 * typed (null: shown as typed).
 */
export function transferReasonWith(reason: string, lookup: (key: string) => string | undefined): TransferReason | null {
  const epp = /^(\d{4}): (.+)$/s.exec(reason) ?? /^REGISTRY_REJECTED (\d{4})$/.exec(reason);
  if (epp) return { kind: 'registry', code: Number(epp[1]), message: epp[2] ?? null };
  const ours = /^cancelled by the gaining registrar: (.+)$/s.exec(reason);
  const text = ours
    ? lookup('domains:transfers.cancelledByUs')?.replace('{{reason}}', () => ours[1] ?? '')
    : /^[A-Z][A-Z0-9_]*$/.test(reason)
      ? (lookup(`domains:transfers.grounds.${reason}`) ?? lookup(`domains:vocab.failure.${reason}`))
      : undefined;
  return text ? { kind: 'words', text } : null;
}
